// Tax Center → Raffle winners: W-2G status per winner, TIN on file (last 4
// only), "send tax form", prize value / type, withholding owed and how it
// was paid, and prize release.

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LoadingState from '../common/LoadingState';
import {
  listWinners, saveWinnerPrize, requestWinnerTin, saveWithholding, setPrizeReleased, errorText, money, toCents,
} from '../../services/taxApi';
import { when } from './format';

function Winner({ w, onChanged }) {
  const { t } = useTranslation();
  const [prize, setPrize] = useState({
    description: w.prizeDescription || '', value: w.prizeValueCents == null ? '' : (w.prizeValueCents / 100).toFixed(2),
    cash: w.prizeIsCash == null ? '' : (w.prizeIsCash ? 'cash' : 'noncash'),
  });
  const wh = w.w2g && w.w2g.withholding;
  const [withholding, setWithholding] = useState({
    method: w.withholdingMethod || '', amount: w.withholdingCents == null ? '' : (w.withholdingCents / 100).toFixed(2),
  });
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const act = async (fn, ok) => {
    setMsg(''); setErr('');
    try { const r = await fn(); setMsg(typeof ok === 'function' ? ok(r) : ok); onChanged(); } catch (e) { setErr(errorText(e, t('tax.saveError', 'Could not save.'))); }
  };

  const savePrize = () => {
    const cents = toCents(prize.value);
    if (Number.isNaN(cents)) { setErr(t('tax.badAmount', 'Enter an amount like 1250.00')); return; }
    act(() => saveWinnerPrize(w.id, { prizeDescription: prize.description, prizeValueCents: cents, prizeIsCash: prize.cash === '' ? null : prize.cash === 'cash' }),
      (r) => (r.tinRequested ? t('tax.winners.savedAndRequested', 'Saved. This prize needs a W-2G, so the winner was sent the tax form.') : t('tax.saved', 'Saved.')));
  };
  const saveWh = () => {
    const cents = toCents(withholding.amount);
    if (withholding.method && (cents === null || Number.isNaN(cents))) { setErr(t('tax.badAmount', 'Enter an amount like 1250.00')); return; }
    act(() => saveWithholding(w.id, { method: withholding.method || null, withholdingCents: cents }), t('tax.saved', 'Saved.'));
  };

  const status = w.w2g ? w.w2g.status : 'incomplete';
  const statusText = {
    reportable: t('tax.winners.reportable', 'W-2G required'),
    not_reportable: t('tax.winners.notReportable', 'No W-2G'),
    incomplete: t('tax.winners.incomplete', 'Needs prize details'),
  };
  const reason = w.w2g && w.w2g.reasons && w.w2g.reasons.length
    ? w.w2g.reasons.map((r) => ({ below_min_proceeds: t('tax.winners.belowMin', 'below the reporting threshold'), odds_multiple_not_met: t('tax.winners.oddsNotMet', 'under the 300× wager test') }[r] || r)).join(', ')
    : '';

  return (
    <li className="tx-winner">
      <div className="tx-winner-head">
        <span><strong>{w.name || w.email}</strong> · {w.raffle} · {w.date}</span>
        <span className={`tx-status ${status}`}>{statusText[status]}{reason ? ` — ${reason}` : ''}</span>
      </div>
      {w.w2g && w.w2g.missing && (
        <p className="tx-err">{t('tax.winners.missing', 'Missing: {{list}}', { list: w.w2g.missing.join(', ').replace(/_/g, ' ') })}</p>
      )}
      <div className="tx-row">
        <label className="fo-field">{t('tax.winners.prize', 'Prize')}
          <input type="text" value={prize.description} onChange={(e) => setPrize({ ...prize, description: e.target.value })} />
        </label>
        <label className="fo-field">{t('tax.winners.value', 'Value (cash or FMV)')}
          <input type="text" inputMode="decimal" value={prize.value} onChange={(e) => setPrize({ ...prize, value: e.target.value })} placeholder="0.00" />
        </label>
        <label className="fo-field">{t('tax.winners.type', 'Prize type')}
          <select value={prize.cash} onChange={(e) => setPrize({ ...prize, cash: e.target.value })}>
            <option value="">{t('tax.winners.useRaffle', 'Use the raffle’s setting')}</option>
            <option value="cash">{t('tax.winners.cash', 'Cash')}</option>
            <option value="noncash">{t('tax.winners.noncash', 'Non-cash (FMV)')}</option>
          </select>
        </label>
        <button type="button" className="lm-btn secondary" onClick={savePrize}>{t('tax.save', 'Save')}</button>
      </div>
      <p className="tx-hint">{t('tax.winners.wager', 'Wager: {{wager}} · Proceeds: {{proceeds}}', { wager: money(w.wagerCents), proceeds: w.w2g && w.w2g.proceedsCents != null ? money(w.w2g.proceedsCents) : '—' })}</p>

      {status === 'reportable' && (
        <>
          <div className="tx-row">
            <div className="fo-field">
              {t('tax.winners.tin', 'Taxpayer ID')}
              <strong>
                {w.tin.onFile
                  ? `${w.tin.type === 'ein' ? '**-***' : '***-**-'}${w.tin.last4} (${w.tin.legalName})`
                  : w.tin.requestSentAt ? t('tax.winners.requested', 'Form sent {{when}}', { when: when(w.tin.requestSentAt) }) : t('tax.winners.notRequested', 'Not on file')}
              </strong>
            </div>
            {!w.tin.onFile && (
              <button type="button" className="lm-btn secondary" onClick={() => act(() => requestWinnerTin(w.id), (r) => (r.emailed ? t('tax.winners.formSent', 'Tax form sent.') : t('tax.winners.formNotSent', 'Link created, but the email could not be sent.')))}>
                {w.tin.requestSentAt ? t('tax.winners.resend', 'Resend tax form') : t('tax.winners.send', 'Send tax form')}
              </button>
            )}
          </div>
          {wh && (
            <div className="tx-owed">
              {wh.type === 'backup'
                ? t('tax.winners.backupOwed', 'Backup withholding applies without a TIN: {{amount}}.', { amount: money(wh.paidByWinnerCents) })
                : t('tax.winners.regularOwed', 'Withholding owed: {{amount}} if the winner pays it.', { amount: money(wh.paidByWinnerCents) })}
              {wh.grossUp && ` ${t('tax.winners.grossUp', 'If your organization pays it for the winner (gross-up): {{amount}}, and reportable winnings become {{total}}.', { amount: money(wh.grossUp.withholdingCents), total: money(wh.grossUp.reportableProceedsCents) })}`}
            </div>
          )}
          <div className="tx-row">
            <label className="fo-field">{t('tax.winners.whMethod', 'Withholding paid by')}
              <select value={withholding.method} onChange={(e) => setWithholding({ ...withholding, method: e.target.value })}>
                <option value="">{t('tax.winners.whNone', 'Not recorded')}</option>
                <option value="paid_by_winner">{t('tax.winners.whWinner', 'Winner')}</option>
                <option value="grossed_up">{t('tax.winners.whGrossUp', 'Organization (grossed up)')}</option>
              </select>
            </label>
            <label className="fo-field">{t('tax.winners.whAmount', 'Amount withheld')}
              <input type="text" inputMode="decimal" value={withholding.amount} onChange={(e) => setWithholding({ ...withholding, amount: e.target.value })} placeholder="0.00" />
            </label>
            <button type="button" className="lm-btn secondary" onClick={saveWh}>{t('tax.winners.record', 'Record')}</button>
            <label className="fo-check">
              <input type="checkbox" checked={!!w.prizeReleasedAt} onChange={(e) => act(() => setPrizeReleased(w.id, e.target.checked), t('tax.saved', 'Saved.'))} />
              {t('tax.winners.released', 'Prize released')}
            </label>
          </div>
          {!w.tin.onFile && !w.prizeReleasedAt && <p className="tx-hint">{t('tax.winners.holdPrize', 'Collect the tax form before releasing the prize.')}</p>}
        </>
      )}
      {msg && <p className="tx-saved" role="status">{msg}</p>}
      {err && <p className="tx-err" role="alert">{err}</p>}
    </li>
  );
}

export default function TaxWinners({ year }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try { setData(await listWinners(year)); } catch (err) { setError(errorText(err, t('tax.winners.loadError', 'Could not load raffle winners.'))); }
  }, [year, t]);

  useEffect(() => { load(); }, [load]);

  return (
    <section className="surface" aria-labelledby="tx-win-title">
      <h4 id="tx-win-title">{t('tax.winners.title', 'Raffle winners in {{year}}', { year })}</h4>
      <p className="surface-muted perf-small">{t('tax.winners.intro', 'A winner needs Form W-2G when the prize, less what the winning entry cost, meets this year’s thresholds. Only the last 4 digits of a taxpayer ID are ever shown.')}</p>
      {error && <div className="note warn">{error}</div>}
      {!data && !error && <LoadingState />}
      {data && (data.winners.length === 0
        ? <p className="surface-muted perf-small">{t('tax.winners.empty', 'No raffle winners this year.')}</p>
        : <ul className="tx-winners">{data.winners.map((w) => <Winner key={w.id} w={w} onChanged={load} />)}</ul>)}
    </section>
  );
}
